import axios from "axios";
import { useEffect, useState } from "react";
import {
  IoEyeOffOutline,
  IoEyeOutline,
  IoLockOpenOutline,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { LIVE_URL } from "../../config/config";
import useAuthToken from "../../hooks/useAuthToken";
import styles from "./css/ResetPassword.module.scss";

const ResetPasswordPage = () => {
  // Extract the stored email from custom hook useAuthToken
  const { email: storedEmail } = useAuthToken();

  // Initialize state variables
  const [email, setEmail] = useState(storedEmail || "");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // useEffect to handle the display and automatic fading of popup messages
  useEffect(() => {
    let timer;
    if (popupMessage) {
      const popupElement = document.getElementById("popup-message");
      if (popupElement) {
        popupElement.style.opacity = "1";
      }
      timer = setTimeout(() => {
        if (popupElement) {
          popupElement.style.opacity = "0";
        }
        setTimeout(() => {
          setPopupMessage("");
        }, 500);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [popupMessage]);

  // Function to handle the submission of the password reset form
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Reset errors
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setPopupMessage("");

    // Validate inputs
    let hasError = false;
    let firstErrorMessage = "";

    if (!password) {
      setPasswordError("Password Is Required");
      if (!firstErrorMessage) firstErrorMessage = "Password Is Required";
      hasError = true;
    }
    if (!confirmPassword) {
      setConfirmPasswordError("Confirm Password Is Required");
      if (!firstErrorMessage)
        firstErrorMessage = "Confirm Password Is Required";
      hasError = true;
    }
    if (password && confirmPassword && password !== confirmPassword) {
      setConfirmPasswordError("Confirm Password Does Not Match");
      if (!firstErrorMessage)
        firstErrorMessage = "Confirm Password Does Not Match";
      hasError = true;
    }

    if (hasError) {
      setPopupMessage(firstErrorMessage);
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${LIVE_URL}/admin/reset-password`,
        { email, password, confirm_password: confirmPassword },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      setPopupMessage(
        "Password Reset Successfully. You Can Now Log In With Your New Password.",
      );
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error) {
      const errors = error.response?.data?.errors || {};

      const passwordErrorsArray = errors?.password || [];
      const passwordErrorMsg = passwordErrorsArray.join(" ");

      setEmailError(errors?.email?.[0] || "");
      setPasswordError(passwordErrorMsg);
      setConfirmPasswordError(errors?.confirm_password?.[0] || "");

      setPopupMessage(
        passwordErrorMsg ||
          errors?.email?.[0] ||
          errors?.confirm_password?.[0] ||
          error.response?.data?.message ||
          "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      {/* Popup message */}
      <div
        id="popup-message"
        className={`${styles.popup_message} ${
          popupMessage.toLowerCase().includes("success")
            ? styles.success
            : styles.error
        }`}
      >
        {popupMessage && <p>{popupMessage}</p>}
      </div>
      <div className={styles.resetPasswordPage}>
        <h2>Reset Password</h2>
        {/* Form to handle password reset */}
        <form onSubmit={handlePasswordSubmit}>
          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <IoLockOpenOutline className={styles.icon} />
              {/* Input for the new password */}
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={passwordError ? styles.error_input : ""}
              />
              <span
                className={styles.eyeIcon}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <IoEyeOutline /> : <IoEyeOffOutline />}
              </span>
            </div>
            {/* {passwordError && <span className={styles.error_message}>{passwordError}</span>} */}
          </div>
          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <IoLockOpenOutline className={styles.icon} />
              {/* Input for confirming the new password */}
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={confirmPasswordError ? styles.error_input : ""}
              />
              <span
                className={styles.eyeIcon}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <IoEyeOutline /> : <IoEyeOffOutline />}
              </span>
            </div>
            {/* {confirmPasswordError && <span className={styles.error_message}>{confirmPasswordError}</span>} */}
          </div>
          {!loading ? (
            <button type="submit">Reset Password</button>
          ) : (
            <button
              disabled
              className={`${styles.loading_button} ${styles.disable_arrow}`}
            >
              <div className={`${styles.loading_btn}`}>
                <div className={`${styles.spinner}`}></div>
              </div>
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
